import React, { useEffect, useState } from 'react'
import { StyleSheet, View } from 'react-native'
import { useSelector } from 'react-redux'
import moment from 'moment'
import v4 from 'uuid/v4'

import ModalHeader from '../ModalHeader'
import { applyPopoverWidth } from '../../../../utils/HelperFunctions'
import { colors } from '../../../styles/global'
import DueDateCalendarModal from '../DueDateCalendarModal/DueDateCalendarModal'
import useWindowSize from '../../../../utils/useWindowSize'
import BackButton from '../GoalMilestoneModal/BackButton'
import Backend from '../../../../utils/BackendBridge'
import { GOALS_OPEN_TAB_INDEX } from '../../../GoalsView/GoalsHelper'
import TabsList, { COMPLETION_TAB, STARTING_TAB } from './TabsList'
import OptionsArea from '../GoalMilestoneModal/OptionsArea'
import { BACKLOG_DATE_NUMERIC } from '../../../TaskListView/Utils/TasksHelper'
import { translate } from '../../../../i18n/TranslationService'
import {
    GOAL_SCHEDULE_MODE_DYNAMIC,
    MILESTONE_TYPE_FIXED,
    MILESTONE_TYPE_LINEAR,
    getDynamicMilestoneOptions,
    normalizeGoalMilestonesConfig,
    normalizeGoalScheduleMode,
} from '../../../../utils/GoalMilestonesHelper'
import { getSafeAreaModalMaxHeight } from '../../../../utils/modalSafeArea'

export default function GoalMilestoneRangeModal({
    projectId,
    closeModal,
    updateMilestoneDateRange,
    startingMilestoneDate,
    completionMilestoneDate,
    setModalWidth,
    setModalHeight,
    ownerId,
    scheduleMode,
}) {
    const [width, height] = useWindowSize()
    const goalsActiveTab = useSelector(state => state.goalsActiveTab)
    const [showCalendar, setShowCalendar] = useState(false)
    const [activeTab, setActiveTab] = useState(COMPLETION_TAB)
    const [milestones, setMilestones] = useState([])
    const project = useSelector(state => state.loggedUserProjectsMap[projectId])
    const dynamicMilestones = getDynamicMilestoneOptions(normalizeGoalMilestonesConfig(project?.goalMilestonesConfig))
    const selectedMilestoneType =
        normalizeGoalScheduleMode(scheduleMode) === GOAL_SCHEDULE_MODE_DYNAMIC
            ? MILESTONE_TYPE_LINEAR
            : MILESTONE_TYPE_FIXED

    const tmpHeight = getSafeAreaModalMaxHeight(height)
    const finalHeight = tmpHeight < 597 ? tmpHeight : 597

    const openCalendar = event => {
        event.preventDefault()
        event.stopPropagation()
        setShowCalendar(true)
    }

    const closeCalendar = () => {
        setShowCalendar(false)
    }

    const updateMilestones = (projectId, milestones) => {
        setMilestones(milestones)
    }

    const onLayout = event => {
        const { width, height } = event.nativeEvent.layout
        setModalWidth(width)
        setModalHeight(height)
    }

    const updateMilestone = (date, milestone = { milestoneType: MILESTONE_TYPE_FIXED }) => {
        updateMilestoneDateRange(
            date,
            activeTab === STARTING_TAB ? 'startingMilestoneDate' : 'completionMilestoneDate',
            milestone
        )
    }

    useEffect(() => {
        const watcherKey = v4()
        Backend.watchMilestones(
            projectId,
            updateMilestones,
            goalsActiveTab !== GOALS_OPEN_TAB_INDEX,
            watcherKey,
            ownerId
        )
        return () => {
            Backend.unwatch(watcherKey)
        }
    }, [])

    const milestoneDate = activeTab === STARTING_TAB ? startingMilestoneDate : completionMilestoneDate

    return (
        <View
            onLayout={setModalWidth && setModalHeight ? onLayout : null}
            style={[localStyles.container, applyPopoverWidth(), { maxHeight: finalHeight }]}
        >
            <ModalHeader
                closeModal={closeModal}
                title={translate('Select Milestones of Goal')}
                description={translate('Select when the goal should be completed and when it started')}
            />

            <TabsList tabStyle={{ width: '50%' }} setActiveTab={setActiveTab} activeTab={activeTab} />

            {showCalendar ? (
                <View>
                    <DueDateCalendarModal
                        closePopover={closeModal}
                        initialDate={
                            milestoneDate === BACKLOG_DATE_NUMERIC
                                ? moment().startOf('day').hour(12).minute(0)
                                : milestoneDate
                        }
                        externalStyle={{ marginHorizontal: -16 }}
                        updateGoalMilestone={date => updateMilestone(date, { milestoneType: MILESTONE_TYPE_FIXED })}
                    />
                    <BackButton onPress={closeCalendar} />
                </View>
            ) : (
                <OptionsArea
                    updateMilestone={updateMilestone}
                    selectedDate={milestoneDate}
                    selectedMilestoneType={selectedMilestoneType}
                    milestones={milestones}
                    dynamicMilestones={dynamicMilestones}
                    openCalendar={openCalendar}
                />
            )}
        </View>
    )
}

const localStyles = StyleSheet.create({
    container: {
        flexDirection: 'column',
        borderRadius: 4,
        backgroundColor: colors.Secondary400,
        boxShadow: '0px 4px 16px rgba(78,93,120,0.56)',
        elevation: 3,
        padding: 16,
        paddingBottom: 8,
    },
})
